import React from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  FiberManualRecord as BulletIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function TermsAndConditions() {
  const navigate = useNavigate();
  const isLoggedIn = localStorage.getItem('token');

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Only show Back button if user is not logged in */}
        {!isLoggedIn ? (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              color: '#6b7280',
              '&:hover': { color: '#374151' }
            }}
            onClick={handleBack}
          >
            <ArrowBackIcon sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="body2">Back</Typography>
          </Box>
        ) : (
          <Box sx={{ width: '80px' }} />
        )}
        
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            color: '#111827',
            flex: 1,
            textAlign: 'center'
          }}
        >
          Terms and Conditions
        </Typography>
        
        {/* Empty div to balance the layout */}
        <Box sx={{ width: '80px' }} />
      </Box>

      {/* Content */}
      <Paper sx={{ p: { xs: 3, md: 4 }, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
        
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'left', pb: 3, borderBottom: '2px solid #e5e7eb' }}>
          <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 600 }}>
            Effective Date: August 29, 2025
          </Typography>
        </Box>

                 {/* 1. Introduction */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             1. Introduction
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             Welcome to <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>. These Terms and Conditions govern your access to and use of <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>, the AI-powered customer support platform designed to integrate with <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify</Box>. By accessing, using, or purchasing services from <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>, you agree to comply with and be bound by these Terms.
           </Typography>
         </Box>

                 {/* 2. Definitions */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             2. Definitions
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>"Customer"</Box> refers to any end-user interacting with <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> through a <Box component="span" sx={{ fontWeight: 'bold' }}>Brand</Box>'s services.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>"Brand"</Box> refers to a business or entity using <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> to enhance their ecommerce operations.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     <Box component="span" sx={{ fontWeight: 'bold' }}>"Services"</Box> refers to the AI-powered chat and customer support features provided by <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
         </Box>

                 {/* 3. Account Registration and Access */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             3. Account Registration and Access
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             To use <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>, you must register for an account that integrates with <Box component="span" sx={{ fontWeight: 'bold' }}>Shopify</Box>. You are responsible for maintaining the confidentiality of your login credentials and for any activity under your account. You agree to provide accurate and complete information during registration and to update it as necessary.
           </Typography>
         </Box>

                 {/* 4. Payment Terms */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             4. Payment Terms
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> operates on a pay-as-you-go model. Fees will be determined based on usage, as outlined during account setup or within your <Box component="span" sx={{ fontWeight: 'bold' }}>Merchant Dashboard</Box>. Charges will be incurred based on the services you use, and payments will be processed accordingly.
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             All payments are non-refundable unless explicitly stated otherwise. You are responsible for ensuring that your payment details are up to date.
           </Typography>
         </Box>

                 {/* 5. Acceptable Use */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             5. Acceptable Use
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             By using <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>, you agree not to engage in any of the following prohibited activities:
           </Typography>
           <List dense sx={{ pl: 2 }}>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Violating any applicable laws, regulations, or third-party rights."
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Distributing harmful code or engaging in malicious activity."
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary={
                   <Box>
                     Disrupting or impairing the service provided by <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box>.
                   </Box>
                 }
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
             <ListItem sx={{ py: 0.5 }}>
               <ListItemIcon sx={{ minWidth: 20 }}>
                 <BulletIcon sx={{ fontSize: 8, color: '#6b7280' }} />
               </ListItemIcon>
               <ListItemText 
                 primary="Attempting to gain unauthorized access to the platform, systems, or data."
                 sx={{ '& .MuiListItemText-primary': { color: '#374151', fontSize: '14px' } }}
               />
             </ListItem>
           </List>
         </Box>

                 {/* 6. Data Privacy and Security */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             6. Data Privacy and Security
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> processes personal data in compliance with our <Box component="span" sx={{ fontWeight: 'bold' }}>Privacy Policy</Box>. While we implement commercially reasonable security measures to protect your data, no system or transmission method is 100% secure. You agree to notify us of any security breaches related to your account.
           </Typography>
         </Box>

                 {/* 7. Intellectual Property */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             7. Intellectual Property
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             All software, technology, and content provided by <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> are the property of <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> or its licensors. <Box component="span" sx={{ fontWeight: 'bold' }}>Brands</Box> retain ownership over their data and trademarks, but grant <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> a limited license to use such data for the purpose of providing services under these Terms.
           </Typography>
         </Box>

                 {/* 8. Termination */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             8. Termination
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             Either party may terminate the use of <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> services by providing written notice to the other party. Upon termination, all access to the platform will be revoked, and data will be handled in accordance with our <Box component="span" sx={{ fontWeight: 'bold' }}>Data Retention Policy</Box>.
           </Typography>
         </Box>

                 {/* 9. Disclaimers and Limitation of Liability */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             9. Disclaimers and Limitation of Liability
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6, mb: 2 }}>
             <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> is provided "as is" and "as available" without any warranties of any kind. We do not guarantee that the service will be error-free, uninterrupted, or meet specific requirements.
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             To the maximum extent permitted by applicable law, <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> is not liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data, profits, or business opportunities.
           </Typography>
         </Box>

                 {/* 10. Governing Law */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             10. Governing Law
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             These Terms are governed by and construed in accordance with the laws of the State of <Box component="span" sx={{ fontWeight: 'bold' }}>Delaware, USA</Box>, without regard to its conflict of law principles. Any disputes under these Terms will be resolved in the courts located within <Box component="span" sx={{ fontWeight: 'bold' }}>Delaware</Box>.
           </Typography>
         </Box>

                 {/* 11. Changes to Terms */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             11. Changes to Terms
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             We reserve the right to modify or update these Terms at any time. Any changes will be effective immediately upon posting to this page. Continued use of <Box component="span" sx={{ fontWeight: 'bold' }}>AgentZena</Box> after such modifications constitutes your acceptance of the updated Terms.
           </Typography>
         </Box>

                 {/* 12. Contact Us */}
         <Box sx={{ mb: 4 }}>
           <Typography variant="h5" sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
             12. Contact Us
           </Typography>
           <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.6 }}>
             If you have any questions about these Terms, please contact us at: <Box component="span" sx={{ fontWeight: 'bold' }}>admin@konnect.bio</Box>
           </Typography>
         </Box>

      </Paper>
    </Container>
  );
}

export default TermsAndConditions;
